import { useCancelInvoiceReportService } from '@/context/services/invoice_reports/InvoiceReportCancel.service';
import { isInvalidError, isNotFoundError } from '@/context/services/type';
import { useCheckInvoiceReportsProtectedResources } from '@/features/invoice_reports/components/hooks/useCheckInvoiceReportsProtectedResources';
import { useInvalidateInvoiceReportDrawer } from '@/context/services/invoice_reports/InvoiceReportDrawer.service';
import {
  extractInvoiceReportErrorMessage,
  getStatusCodeFromError,
} from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback } from 'react';
import { useTriggerFindTargetReport } from '../../List/context/TriggerFindTargetReportContext';

type UseCancelInvoiceReportProps = {
  refetch: () => Promise<unknown>;
  onDrawerClose: () => void;
  selectedId: string | null;
};

const TOAST_DURATION = 5;

export const useCancelInvoiceReport = ({
  refetch,
  onDrawerClose,
  selectedId,
}: UseCancelInvoiceReportProps) => {
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { onTriggerBackToPageOneIfNoData } = useTriggerFindTargetReport();
  const { mutateAsync: cancelInvoiceReport } = useCancelInvoiceReportService();
  const { handleCheckPermission } = useCheckInvoiceReportsProtectedResources();
  const { invalidateDrawerIfOpen } =
    useInvalidateInvoiceReportDrawer(selectedId);

  const cancelReport = useCallback(
    (reportId: string) => async () => {
      try {
        await cancelInvoiceReport(reportId);
        notify.success({
          title: t('invoice_reports.action_menu.cancel_success_message'),
          duration: TOAST_DURATION,
        });
        invalidateDrawerIfOpen(reportId);
        await refetch?.();
        onTriggerBackToPageOneIfNoData();
      } catch (error) {
        const errorMessage = extractInvoiceReportErrorMessage(error, t);
        const statusCode = getStatusCodeFromError(error);

        if (isNotFoundError(statusCode)) {
          if (reportId === selectedId) {
            onDrawerClose();
          }
          notify.error({
            title: toHtmlNode(errorMessage ?? t('system_error')),
            duration: TOAST_DURATION,
          });
          await refetch?.();
          return;
        }

        if (isInvalidError(statusCode)) {
          await handleCheckPermission({
            fnCheckPermission: refetch,
            onCheckSuccess: () => {
              invalidateDrawerIfOpen(reportId);
              notify.error({
                title: toHtmlNode(errorMessage ?? t('system_error')),
                duration: TOAST_DURATION,
              });
              onTriggerBackToPageOneIfNoData();
            },
            onCheckFailure: () => {
              location.reload();
            },
          });
          return;
        }

        await handleCheckPermission({
          fnCheckPermission: refetch,
          onCheckSuccess: () => {
            invalidateDrawerIfOpen(reportId);
            notify.error({
              title: toHtmlNode(errorMessage ?? t('system_error')),
              duration: TOAST_DURATION,
            });
            onTriggerBackToPageOneIfNoData();
          },
          onCheckFailure: () => {
            location.reload();
          },
        });
      }
    },
    [
      cancelInvoiceReport,
      handleCheckPermission,
      invalidateDrawerIfOpen,
      notify,
      onDrawerClose,
      refetch,
      selectedId,
      t,
      onTriggerBackToPageOneIfNoData,
    ]
  );

  return {
    cancelReport,
  };
};
